import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Loading,
  PageHeader,
  Stat,
  Table,
  Tabs,
  Td,
} from '../components/ui/primitives';
import { EngagementPanel } from '../components/analytics/EngagementPanel';
import { BotPanel } from '../components/analytics/BotPanel';
import { ProviderTable } from '../components/deliverability/ProviderTable';
import { dateTime, pct } from '../lib/format';
import type { BotActivity, CampaignRecord, EngagementReport, ProviderRow } from '../lib/types';

interface CampaignReport {
  campaign: CampaignRecord;
  engagement: EngagementReport;
  providers: ProviderRow[];
  bots: BotActivity;
  conversions: {
    conversions: number;
    distinctContacts: number;
    totalValue: number | null;
    clickToConversionRate: number;
  };
  timeline: Array<{
    day: string;
    opensRaw: number;
    opensFiltered: number;
    clicksRaw: number;
    clicksFiltered: number;
    machineOpens: number;
  }>;
  remainingQueued: number;
}

interface Plan {
  audienceSize: number;
  plannedSends: number;
  deferred: number;
  firstSendAt: string | null;
  lastSendAt: string | null;
  spanHours: number;
  byProvider: Array<{ providerFamily: string; count: number }>;
  byIdentity: Array<{ identityId: string; email: string; count: number; dailyCapacity: number }>;
  warnings: string[];
}

interface Review {
  issues: Array<{ code: string; severity: 'error' | 'warning'; message: string }>;
  blocking: boolean;
  links: Array<{ id: string; url: string }>;
}

export function CampaignDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-report', id],
    queryFn: () => api.get<CampaignReport>('/api/campaigns/' + id + '/report'),
    refetchInterval: 30_000,
  });
  const { data: plan } = useQuery({
    queryKey: ['campaign-plan', id],
    queryFn: () => api.get<Plan>('/api/campaigns/' + id + '/plan'),
  });
  const { data: review } = useQuery({
    queryKey: ['campaign-review', id],
    queryFn: () => api.get<Review>('/api/campaigns/' + id + '/review'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['campaign-report', id] });
    void queryClient.invalidateQueries({ queryKey: ['campaign-plan', id] });
  };

  const launch = useMutation({
    mutationFn: (force: boolean) => api.post('/api/campaigns/' + id + '/launch', { force }),
    onSuccess: (r: unknown) => {
      const res = r as { queued: number; deferred: number };
      setMessage(res.queued + ' messages queued, ' + res.deferred + ' deferred to the next window.');
      refresh();
    },
    onError: (err: Error) => setMessage(err.message),
  });
  const pause = useMutation({
    mutationFn: () => api.post('/api/campaigns/' + id + '/pause', { reason: 'Paused by operator' }),
    onSuccess: refresh,
  });
  const resume = useMutation({
    mutationFn: () => api.post('/api/campaigns/' + id + '/resume'),
    onSuccess: refresh,
  });
  const reclassify = useMutation({
    mutationFn: () => api.post('/api/campaigns/' + id + '/reclassify'),
    onSuccess: () => setMessage('Reclassification queued. Figures update once it finishes.'),
  });

  if (isLoading || !data) return <Loading />;

  const c = data.campaign;
  const sending = c.status === 'sending';

  return (
    <>
      <PageHeader
        title={c.name}
        description={c.subject}
        actions={
          <>
            <Button loading={reclassify.isPending} onClick={() => reclassify.mutate()}>
              Re-run bot filter
            </Button>
            {sending ? (
              <Button loading={pause.isPending} onClick={() => pause.mutate()}>
                Pause
              </Button>
            ) : c.status === 'paused' || c.status === 'circuit_broken' ? (
              <Button variant="primary" loading={resume.isPending} onClick={() => resume.mutate()}>
                Resume
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={launch.isPending}
                onClick={() => launch.mutate(false)}
                disabled={c.status === 'completed'}
              >
                Launch
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={sending ? 'info' : c.status === 'circuit_broken' ? 'bad' : 'neutral'}>
          {c.status.replace(/_/g, ' ')}
        </Badge>
        {data.remainingQueued > 0 && <Badge>{data.remainingQueued.toLocaleString()} still queued</Badge>}
        {c.startedAt && <span className="text-xs text-ink-500">Started {dateTime(c.startedAt)}</span>}
      </div>

      {c.pausedReason && (
        <div className="mb-5">
          <Callout tone="bad" title="Campaign stopped">
            {c.pausedReason}
          </Callout>
        </div>
      )}

      {message && (
        <div className="mb-5">
          <Callout tone="info">{message}</Callout>
        </div>
      )}

      {review && review.issues.length > 0 && (
        <div className="mb-5">
          <Callout tone={review.blocking ? 'bad' : 'warn'} title="Pre-flight review">
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {review.issues.map((issue) => (
                <li key={issue.code}>
                  <strong>{issue.severity === 'error' ? 'Blocking: ' : 'Warning: '}</strong>
                  {issue.message}
                </li>
              ))}
            </ul>
            {review.blocking && (
              <div className="mt-3">
                <Button variant="danger" onClick={() => launch.mutate(true)}>
                  Launch anyway
                </Button>
              </div>
            )}
          </Callout>
        </div>
      )}

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'plan', label: 'Send plan' },
          { id: 'providers', label: 'By provider' },
          { id: 'bots', label: 'Automated activity' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'overview' && (
        <div className="space-y-6">
          <EngagementPanel report={data.engagement} timeline={data.timeline} />
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Conversions" value={data.conversions.conversions.toLocaleString()} />
            <Stat
              label="Click to conversion"
              value={pct(data.conversions.clickToConversionRate)}
              hint="Calculated from human clicks only."
            />
            <Stat
              label="Conversion value"
              value={data.conversions.totalValue === null ? '--' : data.conversions.totalValue.toLocaleString()}
            />
          </div>
        </div>
      )}

      {tab === 'plan' && (
        <div className="space-y-5">
          {plan?.warnings.length ? (
            <Callout tone="warn" title="Before you launch">
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {plan.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Callout>
          ) : null}

          {plan && (
            <>
              <div className="grid gap-4 sm:grid-cols-4">
                <Stat label="Eligible audience" value={plan.audienceSize.toLocaleString()} />
                <Stat label="Will send now" value={plan.plannedSends.toLocaleString()} />
                <Stat
                  label="Deferred"
                  value={plan.deferred.toLocaleString()}
                  hint="Beyond today's capacity across every mailbox."
                />
                <Stat label="Spread over" value={plan.spanHours + 'h'} hint={'First ' + dateTime(plan.firstSendAt)} />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <Card title="Per mailbox">
                  <Table head={['Mailbox', 'Messages', 'Remaining capacity today']}>
                    {plan.byIdentity.map((row) => (
                      <tr key={row.identityId}>
                        <Td className="font-mono text-xs">{row.email}</Td>
                        <Td className="tabular-nums">{row.count.toLocaleString()}</Td>
                        <Td className="tabular-nums">{row.dailyCapacity.toLocaleString()}</Td>
                      </tr>
                    ))}
                  </Table>
                </Card>
                <Card
                  title="Per receiving provider"
                  subtitle="Recipients are interleaved so no single estate receives a solid block, which is what triggers throttling."
                >
                  <Table head={['Provider', 'Messages']}>
                    {plan.byProvider.map((row) => (
                      <tr key={row.providerFamily}>
                        <Td className="capitalize">{row.providerFamily}</Td>
                        <Td className="tabular-nums">{row.count.toLocaleString()}</Td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'providers' && (
        <Card>
          <ProviderTable rows={data.providers} />
        </Card>
      )}

      {tab === 'bots' && <BotPanel data={data.bots} />}

      {tab === 'overview' && data.engagement.counts.sent === 0 && (
        <div className="mt-5">
          <EmptyState title="Nothing sent yet" body="Launch the campaign to start collecting engagement." />
        </div>
      )}
    </>
  );
}
